/**
 * Hook to fetch and poll for user's active session.
 * Used for single-game-per-user enforcement and in-game indicator.
 */

import { useState, useEffect, useCallback } from "react";
import { api } from "../lib/api";
import { logger } from "../lib/logger";
import { useAuthStore } from "../store/authStore";
import type { ActiveSession } from "../types/game";

const POLL_INTERVAL_MS = 30000; // 30 seconds

// Custom event for triggering refresh across components
export const ACTIVE_SESSION_CHANGED_EVENT = "activeSessionChanged";

export function triggerActiveSessionRefresh() {
  window.dispatchEvent(new CustomEvent(ACTIVE_SESSION_CHANGED_EVENT));
}

export function useActiveSession() {
  const [activeSession, setActiveSession] = useState<ActiveSession | null>(
    null,
  );
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { isAuthenticated, accessToken } = useAuthStore();

  const fetchSession = useCallback(async () => {
    // Only fetch for authenticated users
    if (!isAuthenticated()) {
      setActiveSession(null);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);
      const response = await api.getMySession();
      setActiveSession(response.active_session);
      logger.debug("active_session_fetched", {
        hasSession: !!response.active_session,
        sessionId: response.active_session?.session_id,
      });
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Failed to fetch session";
      logger.error("active_session_fetch_error", { error: message });
      setError(message);
      setActiveSession(null);
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated]);

  // Initial fetch and polling
  useEffect(() => {
    if (!isAuthenticated()) {
      setActiveSession(null);
      return;
    }

    // Initial fetch
    fetchSession();

    // Set up polling
    const interval = setInterval(fetchSession, POLL_INTERVAL_MS);

    // Listen for manual refresh events (e.g., after leaving a session)
    const handleRefreshEvent = () => {
      fetchSession();
    };
    window.addEventListener(ACTIVE_SESSION_CHANGED_EVENT, handleRefreshEvent);

    return () => {
      clearInterval(interval);
      window.removeEventListener(
        ACTIVE_SESSION_CHANGED_EVENT,
        handleRefreshEvent,
      );
    };
  }, [isAuthenticated, accessToken, fetchSession]);

  // Refresh function for manual updates
  const refresh = useCallback(() => {
    fetchSession();
  }, [fetchSession]);

  return {
    activeSession,
    isLoading,
    error,
    refresh,
  };
}
